interface IScaleHandler {
  imageNaturalHeight: number
  imageClientHeight: number
  scaleFactor: number
  scale: number
  maxPercentage: number
  maxScalePixelDensity: number
  percentageIncrements: number
  percentageSteps: Array<number>
  scaleSteps: Array<number>
}

class ScaleHandler implements IScaleHandler {
  imageNaturalHeight: number
  imageClientHeight: number
  scaleFactor: number
  scale = 1
  maxPercentage = 100
  maxScalePixelDensity = 25
  percentageIncrements = 2.5

  constructor(imageNaturalHeight: number, imageClientHeight: number) {
    this.imageNaturalHeight = imageNaturalHeight || 0
    this.imageClientHeight = imageClientHeight || 0
    this.scaleFactor = imageNaturalHeight / this.maxScalePixelDensity
    this.percentageSteps = this.getPercentageSteps()
    this.scaleSteps = this.getScaleSteps()
  }
  percentageSteps: number[]
  scaleSteps: number[]

  getScaleFromPercentage(percentage: number): number {
    // Exponential scaling formula
    const exponentialFactor = Math.exp((percentage / 100) * Math.log(this.scaleFactor))
    return exponentialFactor
  }

  findClosestPercentage(arr: number[], target: number): number {
    return arr.reduce((prev, curr) => (Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev))
  }

  getPercentageFromScale(scale: number): number {
    // Calculate percentage from scale using the inverse of the exponential formula
    const rawPercentage = (Math.log(scale) / Math.log(this.scaleFactor)) * 100
    return this.findClosestPercentage(this.percentageSteps, rawPercentage)
  }

  getPercentageSteps(): Array<number> {
    if (!this.maxPercentage || !this.percentageIncrements) {
      console.warn('Values for generating percentage steps have not been instantiated.')
      return []
    }
    return Array.from(
      { length: Math.floor(this.maxPercentage / this.percentageIncrements) + 1 },
      (_, i) => i * this.percentageIncrements
    )
  }

  getScaleSteps(): Array<number> {
    if (!this.percentageSteps) {
      console.warn('Values for generating scale steps have not been instantiated.')
      return []
    }
    return this.percentageSteps?.map((percentage: number) => this.getScaleFromPercentage(percentage))
  }

  getScaleIndex() {
    return this.scaleSteps.findIndex((step: number) => step === this.scale)
  }

  getNextScale(): number {
    const scaleIndex = this.getScaleIndex()
    if (scaleIndex === this.scaleSteps.length - 1 || scaleIndex === -1) {
      return this.scale
    }
    const nextScale = this.scaleSteps[scaleIndex + 1]
    this.scale = nextScale
    return nextScale
  }

  getPreviousScale(): number {
    const scaleIndex = this.getScaleIndex()
    if (scaleIndex === 0 || scaleIndex === -1) {
      return this.scale
    }
    const previousScale = this.scaleSteps[scaleIndex - 1]
    this.scale = previousScale
    return previousScale
  }
}

export { ScaleHandler }
